import { Mail, MapPin } from "lucide-react";
import { useState } from "react";
import { envConfig } from "~/lib/env-validation";
import { generateMetaTags, SITE_URL } from "~/lib/seo";

export function meta() {
	return generateMetaTags({
		title: "Kontakt – FirmIndex",
		description: "Máte dotaz nebo potřebujete pomoci? Napište nám a ozveme se vám co nejdříve.",
		canonical: `${SITE_URL}/kontakt`,
	});
}

export default function Kontakt() {
	const [submitted, setSubmitted] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		const formData = new FormData(e.currentTarget);
		setSubmitting(true);
		setError(null);

		try {
			const res = await fetch(`${envConfig.apiBaseUrl}/contact`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					name: formData.get("name"),
					email: formData.get("email"),
					message: formData.get("message"),
				}),
			});

			if (res.status === 429) {
				setError("Příliš mnoho zpráv. Zkuste to prosím později.");
				return;
			}
			if (!res.ok) {
				setError("Zprávu se nepodařilo odeslat. Zkuste to prosím znovu.");
				return;
			}
			setSubmitted(true);
		} catch {
			setError("Zprávu se nepodařilo odeslat. Zkuste to prosím znovu.");
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<>
			{/* Header */}
			<section className="bg-gradient-to-b from-primary-50 to-background px-6 pb-12 pt-16 lg:px-8 lg:pt-24">
				<div className="mx-auto max-w-4xl text-center">
					<h1 className="text-4xl font-extrabold tracking-tight text-text sm:text-5xl">Kontakt</h1>
					<p className="mx-auto mt-4 max-w-2xl text-lg text-muted">
						Máte dotaz nebo potřebujete pomoci? Napište nám a ozveme se vám co nejdříve.
					</p>
				</div>
			</section>

			<section className="px-6 py-20 lg:px-8">
				<div className="mx-auto grid max-w-7xl gap-16 lg:grid-cols-2">
					{/* Contact form */}
					<div>
						<h2 className="text-2xl font-bold text-text">Napište nám</h2>
						<p className="mt-2 text-muted">Vyplňte formulář a my se vám ozveme do 24 hodin.</p>

						{submitted ? (
							<div className="mt-8 rounded-xl border border-secondary-200 bg-secondary-50 p-8 text-center">
								<p className="text-lg font-semibold text-secondary-700">Děkujeme za vaši zprávu!</p>
								<p className="mt-2 text-sm text-secondary-600">Ozveme se vám co nejdříve.</p>
							</div>
						) : (
							<form onSubmit={handleSubmit} className="mt-8 space-y-6">
								<div>
									<label htmlFor="name" className="block text-sm font-medium text-text">
										Jméno
									</label>
									<input
										id="name"
										name="name"
										type="text"
										required
										maxLength={200}
										className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-3 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
										placeholder="Jan Novák"
									/>
								</div>
								<div>
									<label htmlFor="email" className="block text-sm font-medium text-text">
										E-mail
									</label>
									<input
										id="email"
										name="email"
										type="email"
										required
										maxLength={320}
										className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-3 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
										placeholder="jan@firma.cz"
									/>
								</div>
								<div>
									<label htmlFor="message" className="block text-sm font-medium text-text">
										Zpráva
									</label>
									<textarea
										id="message"
										name="message"
										rows={5}
										required
										maxLength={5000}
										className="mt-1 block w-full resize-none rounded-lg border border-gray-200 px-4 py-3 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
										placeholder="Jak vám můžeme pomoci?"
									/>
								</div>
								{error && <p className="text-sm text-destructive">{error}</p>}
								<button
									type="submit"
									disabled={submitting}
									className="rounded-full bg-primary-500 px-8 py-3 text-sm font-semibold text-white hover:bg-primary-600 transition-colors disabled:opacity-50"
								>
									{submitting ? "Odesílání..." : "Odeslat zprávu"}
								</button>
							</form>
						)}
					</div>

					{/* Contact info */}
					<div>
						<h2 className="text-2xl font-bold text-text">Kontaktní údaje</h2>
						<p className="mt-2 text-muted">
							Preferujete přímý kontakt? Využijte některý z následujících způsobů.
						</p>

						<div className="mt-8 space-y-6">
							<div className="flex items-start gap-4">
								<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50">
									<MapPin size={20} className="text-primary-600" />
								</div>
								<div>
									<h3 className="text-sm font-semibold text-text">Adresa</h3>
									<p className="mt-1 text-sm text-muted">
										Recadev s.r.o.
										<br />U Plesenky 3353/15, Martinov
										<br />
										723 00 Ostrava
										<br />
										Česká republika
									</p>
								</div>
							</div>

							<div className="flex items-start gap-4">
								<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50">
									<Mail size={20} className="text-primary-600" />
								</div>
								<div>
									<h3 className="text-sm font-semibold text-text">E-mail</h3>
									<p className="mt-1 text-sm text-muted">info@firmindex.cz</p>
								</div>
							</div>
						</div>

						<div className="mt-12 rounded-xl border border-gray-200 bg-gray-50 p-6">
							<h3 className="text-sm font-semibold text-text">Provozní doba podpory</h3>
							<p className="mt-2 text-sm text-muted">
								Pondělí — Pátek: 9:00 — 17:00
								<br />
								Odpovídáme do 24 hodin v pracovních dnech.
							</p>
						</div>
					</div>
				</div>
			</section>
		</>
	);
}
